import { Controller } from '@hotwired/stimulus'
import { filterMenu, flattenMenuResults, rankResults } from '~/layout/menu_search'
import { clearRecentlyOpened, findActiveLeaf, readRecentlyOpened, recordRecentlyOpened } from '~/layout/recently_opened'

// Contexts that belong to no provider: leaving one restores whatever came before.
const TRANSIENT_PANES = ['menu-pane-search', 'menu-pane-notifications']

export default class extends Controller {
  static targets = ['input', 'menu', 'flat', 'empty']
  static values = { tree: Object, icons: Object }

  connect() {
    if (this.hasTreeValue) recordRecentlyOpened(findActiveLeaf(this.treeValue))

    // listened for here, not through data-action: Stimulus reads the dots in
    // "shown.bs.tab" as a key filter, so the action would never fire
    this._onTabShown = (e) => this._contextShown(e)
    document.addEventListener('shown.bs.tab', this._onTabShown)
    // the server rendered one pill active; that is where Escape goes back to
    this._previousPill = this.element.querySelector('.sidebar-provider-pills a.active')

    this._onKeydown = (e) => e.key === 'Escape' && this._leaveTransient()
    this._onClickOutside = (e) => {
      if (!this.element.contains(e.target)) this._leaveTransient()
    }
    document.addEventListener('keydown', this._onKeydown)
    document.addEventListener('mousedown', this._onClickOutside)

    this._render()
  }

  disconnect() {
    document.removeEventListener('shown.bs.tab', this._onTabShown)
    document.removeEventListener('keydown', this._onKeydown)
    document.removeEventListener('mousedown', this._onClickOutside)
  }

  // A context that is not transient becomes the one to come back to, which is
  // the whole reason to listen.
  _contextShown(event) {
    const pane = event.target.getAttribute('href')?.replace('#', '')
    if (!pane) return

    if (TRANSIENT_PANES.includes(pane)) {
      if (pane === 'menu-pane-search' && this.hasInputTarget) this.inputTarget.focus()
      return
    }
    this._previousPill = event.target
  }

  filter() {
    this._render()
  }

  clear(e) {
    if (e) e.stopPropagation()
    clearRecentlyOpened()
    if (this.hasInputTarget) this.inputTarget.focus()
    this._render()
  }

  // Escape and a click outside are the only ways out of a transient context.
  // Clicking its own pill again keeps you there, like the Mantine half.
  _leaveTransient() {
    const active = this.element.querySelector('.tab-pane.show.active')
    if (!active || !TRANSIENT_PANES.includes(active.id)) return
    if (this.hasInputTarget) this.inputTarget.value = ''
    this._render()
    this._previousPill?.click()
  }

  _render() {
    const query = this.hasInputTarget ? this.inputTarget.value.trim() : ''

    if (query) {
      // no active-provider scoping: search walks every provider plus admin
      const results = this.hasTreeValue ? rankResults(flattenMenuResults(filterMenu(this.treeValue, query)), query) : []
      this._renderFlat(results, {})
      if (this.hasEmptyTarget) this.emptyTarget.hidden = results.length > 0
      return
    }

    const recent = readRecentlyOpened()
    this._renderFlat(recent, { label: 'Recently opened', clearable: recent.length > 0 })
    if (this.hasEmptyTarget) this.emptyTarget.hidden = true
  }

  _renderFlat(items, { label = null, clearable = false }) {
    if (!this.hasFlatTarget) return
    this.flatTarget.replaceChildren()
    if (!items.length && !clearable) return

    if (label) {
      const header = document.createElement('div')
      header.className = 'menu-flat-header'
      const title = document.createElement('span')
      title.textContent = label
      header.appendChild(title)
      this.flatTarget.appendChild(header)
    }

    for (const item of items) {
      const a = document.createElement('a')
      a.className = 'menu-flat-item'
      a.href = item.href

      const icon = document.createElement('span')
      icon.className = 'menu-flat-item-icon'
      const svg = this.hasIconsValue ? this.iconsValue[item.icon] : null
      if (svg) icon.innerHTML = svg
      a.appendChild(icon)

      const body = document.createElement('span')
      body.className = 'menu-flat-item-body'
      const labelEl = document.createElement('span')
      labelEl.className = 'menu-flat-item-label'
      labelEl.textContent = item.label
      body.appendChild(labelEl)
      const path = [item.section, item.groupLabel].filter(Boolean).join(' › ')
      if (path) {
        const pathEl = document.createElement('span')
        pathEl.className = 'menu-flat-item-path'
        pathEl.textContent = path
        body.appendChild(pathEl)
      }
      a.appendChild(body)
      this.flatTarget.appendChild(a)
    }

    if (clearable) {
      const clearBtn = document.createElement('button')
      clearBtn.type = 'button'
      clearBtn.className = 'menu-flat-footer'
      clearBtn.textContent = 'Clear'
      clearBtn.addEventListener('click', (e) => this.clear(e))
      this.flatTarget.appendChild(clearBtn)
    }
  }
}
